/* eslint-disable react/prop-types */
import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "config/api";
import { beklenenBaglamiAyarla, baglamDegisinceCagir } from "config/vekaletBaglami";

const AuthContext = createContext();

// Vekil bağlamında kapanan rol bayrakları: arayüz yalnız malikin ev sahibi
// görünümünü sunar, vekilin kendi rolleri (admin dahil) uygulanmaz.
const VEKIL_BAGLAMINDA_KAPALI = [
  "admin",
  "is_root_admin",
  "is_global_admin",
  "kiraci",
  "usta",
  "emlakci",
  "uyelik",
  "yatirimci",
  "ofis_kullanicisi",
  "ofis_yetkilisi",
];

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [baglamUyarisi, setBaglamUyarisi] = useState(null);
  const navigate = useNavigate();

  // Başlık, yeni kullanıcıyla ilk render'daki isteklerden önce ayarlanmalı:
  // çocukların effect'leri ebeveyninkinden önce çalışır.
  const setUser = useCallback((yeni) => {
    beklenenBaglamiAyarla(yeni);
    setUserState(yeni);
  }, []);

  const durumuYenile = useCallback(async () => {
    const response = await axios.get(`${API_BASE_URL}/auth/status`, { withCredentials: true });
    setUser(response.data.authenticated ? response.data.user : null);
    return response.data.authenticated ? response.data.user : null;
  }, [setUser]);

  useEffect(() => {
    async function loadUser() {
      if (typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap") {
        setLoading(false);
        return;
      }
      try {
        await durumuYenile();
      } catch (err) {
        console.error("Authentication check failed:", err);
      } finally {
        setLoading(false);
      }
    }

    loadUser();
  }, []);

  // Başka sekmede bağlam değişti ya da vekalet düştü (sunucu 412 döndü).
  useEffect(() => {
    baglamDegisinceCagir(async () => {
      try {
        const guncel = await durumuYenile();
        setBaglamUyarisi({
          tur: "warning",
          mesaj: guncel?.vekalet
            ? `Hesap başka bir sekmede değişti: artık ${guncel.vekalet.malik_ad} adına işlem yapıyorsunuz.`
            : "Vekalet bağlamı sona erdi: kendi hesabınızdasınız.",
        });
        navigate("/dashboard");
      } catch (err) {
        console.error("Hesap bağlamı yenilenemedi:", err);
      }
    });
    return () => baglamDegisinceCagir(null);
  }, [durumuYenile, navigate]);

  const login = async (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate("/authentication/sign-in");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  /** Malik bağlamına geç (vekaletId) ya da kendi hesabına dön (null). */
  const vekaletSec = useCallback(
    async (vekaletId) => {
      try {
        await axios.post(
          `${API_BASE_URL}/auth/vekalet-sec`,
          { vekalet_id: vekaletId },
          { withCredentials: true }
        );
        setBaglamUyarisi(null);
      } catch (err) {
        // Örn. vekalet bu arada iptal edildi: liste aşağıda yenilenir.
        setBaglamUyarisi({
          tur: "danger",
          mesaj: err.response?.data?.detail || "Hesap değiştirilemedi.",
        });
      }
      try {
        await durumuYenile();
      } catch (err) {
        console.error("Hesap bağlamı yenilenemedi:", err);
      }
      navigate("/dashboard");
    },
    [durumuYenile, navigate]
  );

  // `user` her zaman giriş yapan gerçek kişidir (profil, şifre, bildirimler).
  // Malik ekranları kimliği `etkinMalikId`den, menü/yönlendirme rolleri
  // `rolKullanicisi`ndan alır. Asıl sınır sunucudaki allow-list; bunlar görünüm.
  const baglam = useMemo(() => {
    const vekalet = user?.vekalet || null;
    let rolKullanicisi = user;
    if (vekalet) {
      rolKullanicisi = { ...user, ev_sahibi: true, vekalet };
      VEKIL_BAGLAMINDA_KAPALI.forEach((alan) => {
        rolKullanicisi[alan] = false;
      });
    }
    return {
      vekalet,
      vekaletler: user?.vekaletler || [],
      etkinMalikId: vekalet ? vekalet.malik_id : user?.user_id,
      rolKullanicisi,
      yazabilir: !vekalet || vekalet.yetki === "tam",
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        vekaletSec,
        baglamUyarisi,
        baglamUyarisiniKapat: () => setBaglamUyarisi(null),
        ...baglam,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
